// Marking up a conversation's transcript. Selecting a passage opens the
// mark-up form with the passage's offsets filled in; clicking a highlighted
// passage shows its annotations, and clicking an annotation's quote finds the
// passage in the transcript.
//
// Offsets count characters (Unicode code points, as Ruby does) from the start
// of the transcript element, whose text is the transcript exactly.
import { Controller } from "@hotwired/stimulus";

const FLASH_CLASSES = ["ring-2", "ring-amber-400"];
const FLASH_MS = 1600;

export default class AnnotatorController extends Controller<HTMLElement> {
  static override targets = ["transcript", "form", "hint", "start", "end", "quote"];

  declare readonly transcriptTarget: HTMLElement;
  declare readonly formTarget: HTMLElement;
  declare readonly hasFormTarget: boolean;
  declare readonly hintTarget: HTMLElement;
  declare readonly hasHintTarget: boolean;
  declare readonly startTarget: HTMLInputElement;
  declare readonly endTarget: HTMLInputElement;
  declare readonly quoteTarget: HTMLElement;

  select(): void {
    if (!this.hasFormTarget) return;

    const range = this.selectedRange();
    if (!range) return;

    const quote = range.toString();
    if (quote.trim() === "") return;

    const before = document.createRange();
    before.selectNodeContents(this.transcriptTarget);
    before.setEnd(range.startContainer, range.startOffset);

    const start = codePoints(before.toString());
    this.startTarget.value = String(start);
    this.endTarget.value = String(start + codePoints(quote));
    this.quoteTarget.textContent = quote;

    this.open();
  }

  cancel(): void {
    this.formTarget.classList.add("hidden");
    if (this.hasHintTarget) this.hintTarget.classList.remove("hidden");
    window.getSelection()?.removeAllRanges();
  }

  // A highlighted passage was clicked: show the annotations covering it.
  focus(event: Event): void {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    if (!window.getSelection()?.isCollapsed) return; // the click ended a selection

    const ids = (event.currentTarget.dataset.annotations ?? "").split(" ").filter(Boolean);
    const cards = ids.map((id) => document.getElementById(id)).filter((card) => card !== null);
    cards[0]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    cards.forEach(flash);
  }

  // An annotation's quote was clicked: find its passage in the transcript.
  reveal({ params }: { params: { id?: string } }): void {
    if (!params.id) return;

    const marks = Array.from(this.transcriptTarget.querySelectorAll<HTMLElement>("mark")).filter((mark) =>
      (mark.dataset.annotations ?? "").split(" ").includes(params.id ?? ""),
    );
    marks[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
    marks.forEach(flash);
  }

  private open(): void {
    this.formTarget.classList.remove("hidden");
    if (this.hasHintTarget) this.hintTarget.classList.add("hidden");
    this.formTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  private selectedRange(): Range | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;

    const range = selection.getRangeAt(0);
    const inside = (node: Node): boolean => this.transcriptTarget.contains(node);
    return inside(range.startContainer) && inside(range.endContainer) ? range : null;
  }
}

function codePoints(text: string): number {
  return Array.from(text).length;
}

function flash(element: HTMLElement): void {
  element.classList.add(...FLASH_CLASSES);
  window.setTimeout(() => element.classList.remove(...FLASH_CLASSES), FLASH_MS);
}
